import { functionLinks } from "../data/navLinks";

const ENTITY_FALLBACKS = {
  order: "/account/order",
  debitNote: "/m/gi/debitNote",
  renewal: "/gi/renewal",
  client: "/account/client",
  payroll: "/management/payroll",
  commissionStatement: "/account/commissionStatement",
  bankTransaction: "/account/bankTransaction",
  user: "/operation/user",
  product: "/operation/product",
  provider: "/operation/provider",
  productType: "/operation/productType",
  productTypeGroup: "/operation/productTypeGroup",
  quiz: "/operation/quiz",
  commissionSchedule: "/management/commissionSchedule",
};

const ROLE_SCOPE_PREFIXES = {
  MANAGEMENT: ["/management/", "/m/", "/account/"],
  OPERATION: ["/operation/", "/gi/"],
  GI: ["/gi/", "/operation/"],
  ACCOUNT: ["/account/", "/management/"],
  CONSULTANT: ["/consultant/"],
};

const KNOWN_PORTAL_PREFIXES = [
  "/consultant/",
  "/management/",
  "/operation/",
  "/account/",
  "/gi/",
  "/m/",
];

const ROLE_SCOPED_FALLBACKS = {
  product: {
    MANAGEMENT: "/management/product",
    OPERATION: "/operation/product",
    GI: "/gi/product",
    ACCOUNT: "/account/product",
    CONSULTANT: "/consultant/product",
    default: "/operation/product",
  },
  orderInvestmentTransaction: {
    CONSULTANT: "/consultant/orderInvestmentTransaction",
    MANAGEMENT: "/management/orderInvestmentTransaction",
    OPERATION: "/operation/orderInvestmentTransaction",
    default: "/operation/orderInvestmentTransaction",
  },
  orderService: {
    CONSULTANT: "/consultant/orderService",
    MANAGEMENT: "/management/orderService",
    OPERATION: "/operation/orderService",
    default: "/operation/orderService",
  },
  payroll: {
    MANAGEMENT: "/management/payroll",
    ACCOUNT: "/account/payroll",
    default: "/management/payroll",
  },
  cpd: {
    MANAGEMENT: "/management/cpd",
    default: "/operation/cpd",
  },
};

const getRoleName = (userRole) => userRole?.name?.toUpperCase() ?? "";

const classifyRoleScope = (userRole) => {
  const name = getRoleName(userRole);
  if (!name) return null;
  if (
    name === "MANAGEMENT" ||
    name.includes("MANAGEMENT") ||
    name === "ADMINISTRATOR" ||
    name === "ADMIN"
  ) {
    return "MANAGEMENT";
  }
  if (
    name === "CONSULTANT" ||
    name.includes("CONSULTANT") ||
    name.includes("ADVISOR") ||
    name.includes("LIFE AGENT") ||
    name.endsWith(" AGENT")
  ) {
    return "CONSULTANT";
  }
  if (name === "GI OPERATION" || name.startsWith("GI ")) return "GI";
  if (name === "OPERATION" || name.includes("OPERATION")) return "OPERATION";
  if (name === "ACCOUNTING" || name === "ACCOUNT" || name.includes("ACCOUNT")) {
    return "ACCOUNT";
  }
  return null;
};

const resolveRoleScopedFallback = (entity, userRole) => {
  const config = ROLE_SCOPED_FALLBACKS[entity];
  if (!config) return undefined;
  const scope = classifyRoleScope(userRole);
  if (scope && config[scope]) return config[scope];
  const roleName = getRoleName(userRole);
  return config[roleName] ?? config.default;
};

const getMcaScopePrefixes = (userRole) => {
  const scope = classifyRoleScope(userRole);
  if (scope && ROLE_SCOPE_PREFIXES[scope]) return ROLE_SCOPE_PREFIXES[scope];
  return ["/management/", "/m/", "/account/", "/operation/", "/gi/", "/consultant/"];
};

const getScopePrefixes = (pathname = "", userRole = null) => {
  if (pathname.startsWith("/mca")) return getMcaScopePrefixes(userRole);
  if (pathname.startsWith("/m/gi")) return ["/m/gi/"];
  if (pathname.startsWith("/management")) return ["/management/", "/m/"];
  if (pathname.startsWith("/account")) return ["/account/"];
  if (pathname.startsWith("/consultant")) return ["/consultant/"];
  if (pathname.startsWith("/operation")) return ["/operation/"];
  if (pathname.startsWith("/gi")) return ["/gi/"];
  return null;
};

const isKnownPortalPath = (path) =>
  Boolean(path) && KNOWN_PORTAL_PREFIXES.some((prefix) => path.startsWith(prefix));

const pathMatchesScope = (path, scopePrefixes) => {
  if (!path || !scopePrefixes?.length) return false;
  return scopePrefixes.some((prefix) => path.startsWith(prefix));
};

const pickScopedPath = (candidates, scopePrefixes) => {
  if (!candidates.length) return undefined;
  if (!scopePrefixes?.length) return candidates[0];
  return candidates.find((path) =>
    scopePrefixes.some((prefix) => path.startsWith(prefix)),
  );
};

/**
 * Resolve the list/detail route for an entity using the current user role.
 * On MCA/portal pages, prefer the role-scoped page over a mismatched entityLink
 * (e.g. consultant catalog vs management product editor).
 * Priority: in-scope or custom entityLinks → granted page links (scope-aware) →
 * role fallback → remaining entityLinks → static fallback.
 */
export function resolveEntityPath(entity, userRole, currentPathname = "") {
  const entityLink = userRole?.entityLinks?.find(
    (entry) => entry.entity === entity,
  )?.link;

  const grantedLinkIds = new Set(
    (userRole?.links ?? [])
      .filter((entry) => (entry.accessRight ?? 0) > 0)
      .map((entry) => entry.link),
  );

  const pathSuffix = `/${entity}`;
  const candidates = functionLinks
    .filter(
      (page) =>
        grantedLinkIds.has(page.linkId) && page.link?.endsWith(pathSuffix),
    )
    .map((page) => page.link);

  const pathname =
    currentPathname ||
    (typeof window !== "undefined" ? window.location.pathname : "");
  const scopePrefixes = getScopePrefixes(pathname, userRole);
  const hasScope = Boolean(scopePrefixes?.length);

  if (!hasScope) {
    if (entityLink) return entityLink;
    if (candidates[0]) return candidates[0];
    return ENTITY_FALLBACKS[entity] ?? resolveRoleScopedFallback(entity, userRole);
  }

  const entityLinkIsCustom = entityLink && !isKnownPortalPath(entityLink);
  const entityLinkIsConsultantCatalog =
    entity === "product" && entityLink === "/consultant/product";
  const isConsultantRole = classifyRoleScope(userRole) === "CONSULTANT";
  if (
    entityLinkIsCustom ||
    (pathMatchesScope(entityLink, scopePrefixes) &&
      (!entityLinkIsConsultantCatalog || isConsultantRole))
  ) {
    return entityLink;
  }

  const scopedGranted = pickScopedPath(candidates, scopePrefixes);
  if (scopedGranted) return scopedGranted;

  const roleFallback = resolveRoleScopedFallback(entity, userRole);
  if (roleFallback) return roleFallback;

  if (entityLink) return entityLink;
  if (candidates[0]) return candidates[0];
  return ENTITY_FALLBACKS[entity];
}
